import React, { useState, useEffect } from 'react';
import { User, Home, Search, Package, FileText, Settings, LogOut, Menu, X, Plus, AlertCircle, Activity, ClipboardList, Wrench } from 'lucide-react';
import PMManagement from './PMManagement';
import FixedAssets from './FixedAssets';
import EOLAssets from './EOLAssets';
import NRMCInventory from './NRMCInventory';
import GoodShepherdInventory from './GoodShepherdInventory';
import GoodShepherdPMs from './GoodShepherdPMs';
import UsersManager from './UsersManager';
import DashboardHome from './DashboardHome';
import PartsInventory from './PartsInventory';
import NRMCPartsInventory from './NRMCPartsInventory';
import ServproEquipment from './ServproEquipment';
import SensorDashboard from './SensorDashboard';
import CertTracking from './CertTracking';
import StaffConversations from './StaffConversations';
import QmpAssistant from './QmpAssistant';
import MagmonInbox from './MagmonInbox';
import RoundupTracker from './RoundupTracker';
import GapAnalysis from './GapAnalysis';

// Equipment Roundup is a deliberate two-person feature (gene id 1, Garyt id 6).
// This mirrors requireRoundupAccess in server.js — the server is the real gate;
// this only decides whether the menu item and screen are shown. Keep the two
// lists in sync. user.id arrives as a number from the login payload but older
// code in this file compares it as a string, so normalise before comparing.
const ROUNDUP_USER_IDS = [1, 6];
const canUseRoundup = (u) => !!u && ROUNDUP_USER_IDS.includes(Number(u.id));

// Christus Good Shepherd is a temporary help engagement worked by exactly two
// people (gene id 1, Garyt id 6). Mirrors GOOD_SHEPHERD_ALLOWED_USER_IDS in
// server.js — the server is the real gate, this only hides the menu. Both
// lists must be edited together.
const GOOD_SHEPHERD_USER_IDS = [1, 6];
const canUseGoodShepherd = (u) => !!u && GOOD_SHEPHERD_USER_IDS.includes(Number(u.id));

// Staff Conversations holds one manager's 1:1 notes about her staff — Kasey
// (id 8) only, NOT every cert_tracker and NOT admins. Mirrors
// STAFF_CONVERSATIONS_ALLOWED_USER_IDS in server.js — the server is the real
// gate, this only hides the menu and screen. Both lists must be edited together.
const STAFF_CONVERSATIONS_USER_IDS = [8];
const canUseStaffConversations = (u) => !!u && STAFF_CONVERSATIONS_USER_IDS.includes(Number(u.id));

// Gap Analysis (service manual repository) is a deliberate two-person feature
// (gene id 1, Garyt id 6) — admins are NOT automatically included. Mirrors
// GAP_ANALYSIS_ALLOWED_USER_IDS in server.js — the server is the real gate,
// this only hides the menu and screen. Both lists must be edited together.
const GAP_ANALYSIS_USER_IDS = [1, 6];
const canUseGapAnalysis = (u) => !!u && GAP_ANALYSIS_USER_IDS.includes(Number(u.id));

const App = () => {
  const [user, setUser] = useState(null);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  const [counts, setCounts] = useState({
    totalPMs: 0,
    currentMonthPMs: 0,
    inventory: 0,
    alarisInv: 3,
    foodPumpInv: 5
  });

  const [eolAlerts, setEolAlerts] = useState({
    expired_files: 0,
    expiring_files: 0,
    total_alerts: 0
  });

  // Mobile detection and responsive handling
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && sidebarOpen) {
        const sidebar = document.querySelector('.sidebar');
        const menuToggle = document.querySelector('.menu-toggle');
        
        if (sidebar && !sidebar.contains(event.target) && 
            menuToggle && !menuToggle.contains(event.target)) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, sidebarOpen]);

  useEffect(() => {
    const checkStoredAuth = () => {
      try {
        const storedUser = localStorage.getItem('pmplus_user');
        const storedActiveSection = localStorage.getItem('pmplus_activeSection');
        
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          console.log('=== RESTORING USER FROM STORAGE ===', userData);
          setUser(userData);
          
          // Set default section based on user role
          if (userData.user_role === 'maintenance') {
            setActiveSection('nrmc-inventory');
          } else if (userData.user_role === 'servpro') {
            setActiveSection('servpro-equipment');
          } else if (userData.user_role === 'regional_cardiology') {
            setActiveSection('sensor-dashboard');
          } else if (userData.user_role === 'cert_tracker') {
            setActiveSection('cert-tracking');
          } else if (userData.user_role === 'qms_user') {
            setActiveSection('qmp-assistant');
          } else if (storedActiveSection) {
            setActiveSection(storedActiveSection);
          }
        }
      } catch (error) {
        console.error('Error loading stored authentication:', error);
        localStorage.removeItem('pmplus_user');
        localStorage.removeItem('pmplus_activeSection');
      }
      setAuthLoading(false);
    };

    checkStoredAuth();
  }, []);

  useEffect(() => {
    if (user && activeSection) {
      localStorage.setItem('pmplus_activeSection', activeSection);
    }
  }, [activeSection, user]);

  const fetchCounts = async () => {
    try {
      console.log('=== APP: FETCHING COUNTS ===');
      const response = await fetch('/api/dashboard/counts');
      const data = await response.json();
      console.log('=== APP: COUNTS RECEIVED ===', data);
      
      setCounts(prevCounts => ({
        ...prevCounts,
        totalPMs: data.totalPMs || 0,
        currentMonthPMs: data.currentMonthPMs || 0,
        inventory: data.inventory || 0,
        alarisInv: data.alarisInv || prevCounts.alarisInv,
        foodPumpInv: data.foodPumpInv || prevCounts.foodPumpInv
      }));
    } catch (error) {
      console.error('=== APP: FAILED TO FETCH COUNTS ===', error);
    }
  };

  const fetchEOLAlerts = async () => {
    try {
      console.log('=== APP: FETCHING EOL ALERTS ===');
      const response = await fetch('/api/eol-assets/counts');
      const data = await response.json();
      console.log('=== APP: EOL ALERTS RECEIVED ===', data);
      
      setEolAlerts({
        expired_files: data.expired_files || 0,
        expiring_files: data.expiring_files || 0,
        total_alerts: data.total_alerts || 0
      });
    } catch (error) {
      console.error('=== APP: FAILED TO FETCH EOL ALERTS ===', error);
    }
  };

  useEffect(() => {
    if (user && user.user_role !== 'maintenance') {
      console.log('=== APP: USER LOGGED IN, FETCHING INITIAL COUNTS ===');
      fetchCounts();
      fetchEOLAlerts();
    }
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });
      
      const data = await response.json();
      if (data.success) {
        console.log('=== LOGIN SUCCESS, STORING USER ===', data.user);
        setUser(data.user);
        localStorage.setItem('pmplus_user', JSON.stringify(data.user));
        if (data.token) {
          localStorage.setItem('pmplus_token', data.token);
        }
        
        // Set default active section based on user role
        if (data.user.user_role === 'maintenance') {
          setActiveSection('nrmc-inventory');
        } else if (data.user.user_role === 'servpro') {
          setActiveSection('servpro-equipment');
        } else if (data.user.user_role === 'regional_cardiology') {
          setActiveSection('sensor-dashboard');
        } else if (data.user.user_role === 'cert_tracker') {
          setActiveSection('cert-tracking');
        } else if (data.user.user_role === 'qms_user') {
          setActiveSection('qmp-assistant');
        } else {
          setActiveSection('home');
        }
        
        setCredentials({ username: '', password: '' });
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (error) {
      setError('Connection failed');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    console.log('=== LOGGING OUT USER ===');
    setUser(null);
    setActiveSection('home');
    localStorage.removeItem('pmplus_user');
    localStorage.removeItem('pmplus_token');
    localStorage.removeItem('pmplus_activeSection');
    setCounts({
      totalPMs: 0,
      currentMonthPMs: 0,
      inventory: 0,
      alarisInv: 3,
      foodPumpInv: 5
    });
    setEolAlerts({
      expired_files: 0,
      expiring_files: 0,
      total_alerts: 0
    });
  };

  const Badge = ({ count }) => (
    count > 0 ? <span className="nav-badge">{count}</span> : null
  );

  const MenuItem = ({ href, icon: Icon, children, badge, onClick }) => (
    <li className="nav-item">
      <button
        onClick={() => {
          if (onClick) {
            onClick();
          } else {
            setActiveSection(href);
          }
          if (isMobile) {
            setSidebarOpen(false);
          }
        }}
        className={`nav-button ${activeSection === href ? 'active' : ''}`}
      >
        <Icon className="nav-icon" size={20} />
        <span className="nav-text">{children}</span>
        {badge}
      </button>
    </li>
  );

  const SectionHeader = ({ children }) => (
    <div className="section-header">{children}</div>
  );

  if (authLoading) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-icon">
              <Package size={32} color="white" />
            </div>
            <h2 className="login-title">PM Plus Lookup</h2>
            <p className="login-subtitle">Loading...</p>
          </div>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #2563eb',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-icon">
              <Package size={32} color="white" />
            </div>
            <h2 className="login-title">PM Plus Lookup</h2>
            <p className="login-subtitle">Sign in to your account</p>
          </div>

          <form onSubmit={handleLogin}>
            {error && (
              <div className="error-alert">
                <AlertCircle size={20} color="#dc2626" />
                <p className="error-text">{error}</p>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Username</label>
              <div className="input-container">
                <User className="input-icon" size={20} />
                <input
                  type="text"
                  required
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  className="form-input"
                  placeholder="Enter your username"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-container">
                <Settings className="input-icon" size={20} />
                <input
                  type="password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  className="form-input"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className={`sidebar ${!sidebarOpen ? 'collapsed' : ''}`}>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <div className="brand-icon">
                <Package size={20} color="white" />
              </div>
              <span className="brand-text">PM Plus Lookup</span>
            </div>
          </div>

          <div className="user-profile">
            <div className="profile-content">
              <div className="profile-avatar">
                <User size={24} color="white" />
              </div>
              <div>
                <div className="profile-welcome">Welcome,</div>
                <div className="profile-name">{user.fullName}</div>
              </div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <ul className="nav-list">
            {/* Servpro User Section - Only for Servpro users */}
              {user.user_role === 'servpro' && (
                <>
                  <SectionHeader>Servpro Equipment</SectionHeader>
                  <MenuItem href="servpro-equipment" icon={Package}>
                    Equipment Status
                  </MenuItem>
                </>
              )}

            {/* MAINTENANCE USERS - Only see NRMC Inventory */}
              {user.user_role === 'maintenance' && (
                <>
                  <MenuItem href="nrmc-inventory" icon={Package}>
                    NRMC Inventory
                    <Badge count={counts.inventory} />
                  </MenuItem>
                  <MenuItem href="nrmc-parts" icon={Package}>
                    NRMC Parts Inventory
                  </MenuItem>
                  <SectionHeader>Environmental Monitoring</SectionHeader>
                  <MenuItem href="sensor-dashboard" icon={Activity}>
                    Sensor Dashboard
                  </MenuItem>
                </>
              )}
              {/* Regional Cardiology Menu Items */}
              {user.user_role === 'regional_cardiology' && (
                <>
                  <SectionHeader>Environmental Monitoring</SectionHeader>
                  <MenuItem href="sensor-dashboard" icon={Activity}>
                    Sensor Dashboard
                  </MenuItem>
                </>
              )}

              {/* Cert Tracker Menu Items */}
              {user.user_role === 'cert_tracker' && (
                <>
                  <SectionHeader>Cert Tracking</SectionHeader>
                  <MenuItem href="cert-tracking" icon={FileText}>
                    Certifications
                  </MenuItem>
                  {canUseStaffConversations(user) && (
                    <MenuItem href="staff-conversations" icon={ClipboardList}>
                      Staff Conversations
                    </MenuItem>
                  )}
                </>
              )}

              {/* QMS User Menu Items */}
              {user.user_role === 'qms_user' && (
                <>
                  <SectionHeader>Quality System</SectionHeader>
                  <MenuItem href="qmp-assistant" icon={FileText}>
                    QMS Assistant
                  </MenuItem>
                </>
              )}

              {/* NON-MAINTENANCE USERS - See everything else */}
              {!['maintenance', 'regional_cardiology', 'servpro', 'cert_tracker', 'qms_user'].includes(user.user_role) && (
                <>
                  {/* Everyone Section - All non-maintenance users can see these */}
                  <MenuItem href="fixed-assets" icon={Home}>
                    NRMC Fixed Assets
                  </MenuItem>
                  <MenuItem href="eol-assets" icon={FileText}>
                    NRMC EOL Assets
                  </MenuItem>
                  <MenuItem href="nrmc-inventory" icon={Package}>
                    NRMC Inventory
                    <Badge count={counts.inventory} />
                  </MenuItem>
                  <MenuItem href="nrmc-parts" icon={Package}>
                    NRMC Parts Inventory
                  </MenuItem>

                  {/* PM Management Section - Only for PM Users and Admins */}
                  {(user.user_role === 'pm_user' || user.user_role === 'admin' || user.canView === '1') && (
                    <>
                      <SectionHeader>PM Management</SectionHeader>
                      <MenuItem href="home" icon={Home}>
                        Home
                      </MenuItem>
                      <MenuItem href="natch-pm" icon={Settings}>
                        Natch PM
                        <Badge count={counts.currentMonthPMs} />
                      </MenuItem>
                      <MenuItem href="my-files" icon={FileText}>
                        My Files
                      </MenuItem>

                      <SectionHeader>Management Items</SectionHeader>
                      <MenuItem href="pm-not-needed" icon={Settings}>
                        PM Not Needed
                      </MenuItem>
                      <MenuItem href="parts-inventory" icon={Package}>
                        BioMed Parts Inventory
                      </MenuItem>
                    </>
                  )}

                  {/* Admin Section - Only for Administrators */}
                  {(user.user_role === 'admin' || user.isAdmin === 'Y') && (
                    <>
                      <SectionHeader>Protected Items & Completed</SectionHeader>
                      <MenuItem href="natch-completed" icon={FileText}>
                        Natch Completed
                      </MenuItem>
                      
                      <SectionHeader>Protected Management Items</SectionHeader>
                      <MenuItem href="users-manager" icon={User}>
                        System Users Manager
                      </MenuItem>

                      <SectionHeader>Environmental Monitoring</SectionHeader>
                      <MenuItem href="sensor-dashboard" icon={Activity}>
                        Sensor Dashboard
                      </MenuItem>
                      <MenuItem href="magmon-inbox" icon={Activity}>
                        MagMon Inbox
                      </MenuItem>
                    </>
                  )}

                  {/* QMS Assistant - admins only here (qms_user lands via their dedicated block above) */}
                  {(user.user_role === 'admin' || user.isAdmin === 'Y') && (
                    <>
                      <SectionHeader>Quality System</SectionHeader>
                      <MenuItem href="qmp-assistant" icon={FileText}>
                        QMS Assistant
                      </MenuItem>
                    </>
                  )}

                  {canUseRoundup(user) && (
                    <>
                      <SectionHeader>Equipment Roundup</SectionHeader>
                      <MenuItem href="equipment-roundup" icon={ClipboardList}>
                        Equipment Roundup
                      </MenuItem>
                    </>
                  )}

                  {canUseGapAnalysis(user) && (
                    <>
                      <SectionHeader>Gap Analysis</SectionHeader>
                      <MenuItem href="gap-analysis" icon={FileText}>
                        Gap Analysis
                      </MenuItem>
                    </>
                  )}

                  {canUseGoodShepherd(user) && (
                    <>
                      <SectionHeader>Good Shepherd</SectionHeader>
                      <MenuItem href="gs-inventory" icon={Package}>
                        Good Shepherd Inventory
                      </MenuItem>
                      <MenuItem href="gs-pms" icon={Wrench}>
                        Good Shepherd PMs
                      </MenuItem>
                    </>
                  )}

                  {/* Owner Only Section */}
                  {(user.id === "213" || user.id === "2") && (
                    <>
                      <SectionHeader>Protected Owner Items</SectionHeader>
                    </>
                  )}
                </>
              )}
            </ul>
          </nav>
        </div>
      </div>

      <div className="main-content">
        <header className="top-nav">
          <div className="nav-header">
            <div className="nav-left">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="menu-toggle"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <h1 className="page-title">
                {activeSection.replace('-', ' ')}
              </h1>
            </div>

            <div className="user-dropdown">
              <button className="dropdown-button">
                <div className="dropdown-avatar">
                  <User size={16} color="white" />
                </div>
                <span className="dropdown-name">{user.fullName}</span>
              </button>
              
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  {user.user_role === 'maintenance' ? 'Maintenance' : 
                   user.isAdmin === 'Y' ? 'Administrator' : 'User'} Role
                </div>
                <button className="dropdown-item">Profile</button>
                <button className="dropdown-item">Files</button>
                <button className="dropdown-item">Dashboard</button>
                <button 
                  onClick={handleLogout}
                  className="dropdown-item logout"
                >
                  <LogOut size={16} style={{marginRight: '0.5rem'}} />
                  Log Out
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="main-area">
          <div className="content-container">
            {/* Maintenance users get redirected to NRMC Inventory */}
            {user.user_role === 'maintenance' && activeSection === 'home' && (
              <div className="content-card">
                <h2 className="content-title">Welcome, Maintenance Team</h2>
                <p className="content-text">
                  You have access to NRMC Inventory and Parts. Please select one of the available sections from the menu.
                </p>
                <button 
                  onClick={() => setActiveSection('nrmc-inventory')}
                  style={{ 
                    padding: '0.75rem 1.5rem', 
                    background: '#2563eb', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.375rem', 
                    cursor: 'pointer',
                    marginTop: '1rem'
                  }}
                >
                  Go to NRMC Inventory
                </button>
              </div>
            )}

            {/* Regional Cardiology users welcome message */}
              {user.user_role === 'regional_cardiology' && activeSection === 'home' && (
                <div className="content-card">
                  <h2 className="content-title">Welcome, Regional Cardiology</h2>
                  <p className="content-text">
                    You have access to the Environmental Monitoring Dashboard for the GE MyoSpec camera room. 
                    Please use the menu to navigate to the Sensor Dashboard to view real-time temperature and humidity data.
                  </p>
                  <button 
                    onClick={() => setActiveSection('sensor-dashboard')}
                    style={{ 
                      padding: '0.75rem 1.5rem', 
                      background: '#2563eb', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '0.375rem', 
                      cursor: 'pointer',
                      marginTop: '1rem'
                    }}
                  >
                    Go to Sensor Dashboard
                  </button>
                </div>
              )}

            {/* Cert Tracker users welcome message */}
            {user.user_role === 'cert_tracker' && activeSection === 'home' && (
              <div className="content-card">
                <h2 className="content-title">Welcome, Cert Tracker</h2>
                <p className="content-text">
                  You have access to the Cert Tracking page. Please use the menu to navigate to Certifications.
                </p>
                <button
                  onClick={() => setActiveSection('cert-tracking')}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    marginTop: '1rem'
                  }}
                >
                  Go to Certifications
                </button>
              </div>
            )}

            {/* Servpro users welcome message */}
            {user.user_role === 'servpro' && activeSection === 'home' && (
              <div className="content-card">
                <h2 className="content-title">Welcome, Servpro Team</h2>
                <p className="content-text">
                  You have access to the Servpro Equipment Status page. 
                  Please use the menu to navigate to Equipment Status to view and update equipment.
                </p>
                <button 
                  onClick={() => setActiveSection('servpro-equipment')}
                  style={{ 
                    padding: '0.75rem 1.5rem', 
                    background: '#2563eb', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.375rem', 
                    cursor: 'pointer',
                    marginTop: '1rem'
                  }}
                >
                  Go to Equipment Status
                </button>
              </div>
            )}

            {/* Home dashboard - only show for PM users and admins */}
            {activeSection === 'home' && (user.user_role === 'pm_user' || user.user_role === 'admin' || user.canView === '1') && (
              <DashboardHome 
                user={user} 
                onNavigate={setActiveSection}
              />
            )}
            
            {/* Asset Manager users get redirected to fixed-assets if they try to access home */}
            {activeSection === 'home' && user.user_role === 'asset_manager' && (
              <div className="content-card">
                <h2 className="content-title">Welcome, Asset Manager</h2>
                <p className="content-text">
                  You have access to manage Fixed Assets, EOL Assets, and Inventory. 
                  Please select one of the available sections from the menu.
                </p>
                <button 
                  onClick={() => setActiveSection('fixed-assets')}
                  style={{ 
                    padding: '0.75rem 1.5rem', 
                    background: '#2563eb', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.375rem', 
                    cursor: 'pointer',
                    marginTop: '1rem'
                  }}
                >
                  Go to Fixed Assets
                </button>
              </div>
            )}

            {/* Available to non-maintenance users */}
            {activeSection === 'fixed-assets' && user.user_role !== 'maintenance' && (
              <FixedAssets user={user} />
            )}

            {/* Sensor Dashboard - available to maintenance and admins and regional_cardiology */}
            {activeSection === 'sensor-dashboard' && (user.user_role === 'regional_cardiology' || user.user_role === 'maintenance' || user.user_role === 'admin' || user.isAdmin === 'Y') && (
              <SensorDashboard user={user} />
            )}

            {/* MagMon Inbox - admin only for now (discovery phase) */}
            {activeSection === 'magmon-inbox' && (user.user_role === 'admin' || user.isAdmin === 'Y') && (
              <MagmonInbox user={user} />
            )}

            {/* Equipment Roundup - hardcoded two-user allowlist, see canUseRoundup */}
            {activeSection === 'equipment-roundup' && canUseRoundup(user) && (
              <RoundupTracker />
            )}

            {/* Gap Analysis - hardcoded two-user allowlist, see canUseGapAnalysis */}
            {activeSection === 'gap-analysis' && canUseGapAnalysis(user) && (
              <GapAnalysis />
            )}

            {/* Cert Tracking - available to cert_tracker and admins */}
            {activeSection === 'cert-tracking' && (user.user_role === 'cert_tracker' || user.user_role === 'admin' || user.isAdmin === 'Y') && (
              <CertTracking user={user} />
            )}

            {/* Staff Conversations - hardcoded one-user allowlist, see canUseStaffConversations */}
            {activeSection === 'staff-conversations' && canUseStaffConversations(user) && (
              <StaffConversations user={user} />
            )}

            {activeSection === 'qmp-assistant' && (user.user_role === 'admin' || user.isAdmin === 'Y' || user.user_role === 'qms_user') && (
              <QmpAssistant user={user} />
            )}

            {activeSection === 'eol-assets' && user.user_role !== 'maintenance' && (
              <EOLAssets user={user} />
            )}

            {/* Servpro Equipment - available to servpro users only */}
            {activeSection === 'servpro-equipment' && user.user_role === 'servpro' && (
              <ServproEquipment user={user} />
            )}

            {/* NRMC Inventory - available to all users including maintenance */}
            {activeSection === 'nrmc-inventory' && (
              <NRMCInventory user={user} onCountsChange={fetchCounts} />
            )}

            {/* Good Shepherd - hardcoded two-user allowlist, see canUseGoodShepherd.
                The server gates /api/gs-* independently; this is menu/render only. */}
            {activeSection === 'gs-inventory' && canUseGoodShepherd(user) && (
              <GoodShepherdInventory user={user} onCountsChange={fetchCounts} />
            )}

            {activeSection === 'gs-pms' && canUseGoodShepherd(user) && (
              <GoodShepherdPMs onCountsChange={fetchCounts} user={user} />
            )}

            {/* NRMC Parts Inventory - available to all users including maintenance */}
            {activeSection === 'nrmc-parts' && (
              <NRMCPartsInventory user={user} />
            )}

            {/* BioMed Parts Inventory - NOT available to maintenance users */}
            {activeSection === 'parts-inventory' && user.user_role !== 'maintenance' && (user.user_role === 'pm_user' || user.user_role === 'admin' || user.canView === '1') && (
              <PartsInventory user={user} />
            )}

            {/* PM Management - restricted access, NOT available to maintenance */}
            {activeSection === 'natch-pm' && user.user_role !== 'maintenance' && (user.user_role === 'pm_user' || user.user_role === 'admin' || user.canView === '1') && (
              <PMManagement onCountsChange={fetchCounts} />
            )}
            
            {/* Admin only - NOT available to maintenance */}
            {activeSection === 'users-manager' && user.user_role !== 'maintenance' && (user.user_role === 'admin' || user.isAdmin === 'Y') && (
              <UsersManager user={user} />
            )}

            {/* Access denied message for restricted sections */}
            {(
              // Maintenance users trying to access non-NRMC Parts sections
              (user.user_role === 'maintenance' && !['nrmc-parts'].includes(activeSection)) ||
              // Servpro users trying to access non-servpro sections
              (user.user_role === 'servpro' && activeSection !== 'servpro-equipment') ||
              // Regional Cardiology trying to access non-sensor sections
              (user.user_role === 'regional_cardiology' && activeSection !== 'sensor-dashboard') ||
              // Cert Tracker trying to access non-cert sections
              (user.user_role === 'cert_tracker' && activeSection !== 'cert-tracking' &&
                !(activeSection === 'staff-conversations' && canUseStaffConversations(user))) ||
              // Staff Conversations is allowlisted by user id, whatever the role
              (activeSection === 'staff-conversations' && !canUseStaffConversations(user)) ||
              // Gap Analysis is allowlisted by user id, whatever the role
              (activeSection === 'gap-analysis' && !canUseGapAnalysis(user)) ||
              // Other access restrictions
              (activeSection === 'home' && user.user_role === 'basic') ||
              (activeSection === 'natch-pm' && !(user.user_role === 'pm_user' || user.user_role === 'admin' || user.canView === '1')) ||
              (activeSection === 'parts-inventory' && !(user.user_role === 'pm_user' || user.user_role === 'admin' || user.canView === '1')) ||
              (activeSection === 'users-manager' && !(user.user_role === 'admin' || user.isAdmin === 'Y'))
            ) && (
              <div className="content-card">
                <h2 className="content-title">Access Restricted</h2>
                <p className="content-text">
                  You don't have permission to access this section. Please contact your administrator if you need access.
                </p>
              </div>
            )}

            {/* Default content for unimplemented sections */}
            {activeSection !== 'home' && 
             activeSection !== 'natch-pm' && 
             activeSection !== 'fixed-assets' && 
             activeSection !== 'eol-assets' && 
             activeSection !== 'nrmc-inventory' && 
             activeSection !== 'parts-inventory' &&
             activeSection !== 'nrmc-parts' &&
             activeSection !== 'servpro-equipment' &&
             activeSection !== 'sensor-dashboard' &&
             activeSection !== 'magmon-inbox' &&
             activeSection !== 'cert-tracking' &&
             activeSection !== 'staff-conversations' &&
             activeSection !== 'qmp-assistant' &&
             activeSection !== 'equipment-roundup' &&
             activeSection !== 'gap-analysis' &&
             activeSection !== 'gs-inventory' &&
             activeSection !== 'gs-pms' &&
             activeSection !== 'users-manager' && (
              <div className="content-card">
                <h2 className="content-title">
                  {activeSection.replace('-', ' ')} Page
                </h2>
                <p className="content-text">
                  Content for {activeSection.replace('-', ' ')} will be implemented here.
                  This is where we'll build out each specific functionality.
                </p>
              </div>
            )}
          </div>
        </main>

        <footer className="footer">
          <div className="footer-content">
            <div>PM Plus Lookup Management System</div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default App;